// External routing links (kept as existing external URLs per client request)
export const ENROLL_URL = 'https://griefassessment.agcthefixer.info/contact-724806';
export const ASSESSMENT_URL = 'https://griefassessment.agcthefixer.info/firststep';

// The four foundational M.O.V.E.™ modules (the engine that never changes)
export const MOVE_MODULES = [
  {
    number: '01',
    title: 'Regulation & Safety',
    description:
      'Stabilize the nervous system, establish a sense of internal and relational safety, and build the capacity for deeper grief work.',
  },
  {
    number: '02',
    title: 'Identity & Meaning',
    description:
      'Explore how loss has disrupted your sense of self, and begin the work of reconstructing meaning and purpose.',
  },
  {
    number: '03',
    title: 'Voice & Boundaries',
    description:
      'Articulate your grief, set limits in relationships, and communicate your needs with clarity and confidence.',
  },
  {
    number: '04',
    title: 'Strategic Re-Engagement',
    description:
      'Move forward with intention, rebuild life structures, and integrate the loss into a renewed sense of identity and direction.',
  },
];

// The four Grief Pathways (where the framework is tailored to your type of loss)
export const GRIEF_PATHWAYS = [
  {
    title: 'Loss of a Loved One',
    description:
      'Honors the emotional, relational, and identity-based consequences of bereavement and the non-linear nature of grief.',
  },
  {
    title: 'Loss of a Pet',
    description:
      'Takes pet loss seriously, acknowledging the neurobiological reality of the human-animal bond.',
  },
  {
    title: 'Breakup / Divorce',
    description:
      'Addresses the loss of a person, a shared future, identity, home, and social network after a relationship ends.',
  },
  {
    title: 'Loss of Career / Identity',
    description:
      'Supports the grief that follows layoff, retirement, business failure, or professional identity collapse.',
  },
];

// The five M.O.V.E.™ products, ordered as a value ladder (entry → flagship)
export const PROGRAMS = [
  {
    slug: 'foundations-introduction',
    name: 'M.O.V.E.™ Foundations Introduction',
    badge: 'Start Here',
    price: '$27',
    priceNote: 'one-time',
    duration: '90-minute live workshop',
    tagline: 'Your first step into structured grief transformation.',
    image: '/images/programs/foundations-introduction.jpg',
    imageAlt: 'A woman pausing in peaceful, hopeful reflection',
    color: 'from-[#2D7D7B] to-[#1F5A58]',
    accent: '#2D7D7B',
    intro:
      'A 90-minute live workshop and the gateway into the full M.O.V.E.™ experience. Get oriented to the framework, identify your grief pathway, and take a clear first step from surviving toward intentional reintegration.',
    glance: [
      { label: 'Format', value: 'Live workshop (virtual)' },
      { label: 'Length', value: '90 minutes' },
      { label: 'Commitment', value: 'Single session' },
      { label: 'Best For', value: 'Exploring M.O.V.E.™' },
    ],
    includes: [
      '90 minutes of live facilitation with Dr. Angelia Griffin',
      'M.O.V.E.™ Overview Guide — digital download',
      'Grief Pathway self-assessment (Loved One · Divorce · Pet · Career)',
      'Access to next-step program details and early-enrollment pricing',
    ],
    audienceTitle: "Who It's For",
    audience: [
      'Anyone experiencing loss — a loved one, divorce, a pet, or a career',
      'People who feel stuck, overwhelmed, or unable to move forward',
      'Those ready to move from surviving to intentional reintegration',
      'Anyone exploring the M.O.V.E.™ approach before a deeper commitment',
    ],
    curriculum: null,
    note: 'This workshop is the gateway into the full M.O.V.E.™ experience. Limited seats available each session.',
    ctaLabel: 'Register Now',
  },
  {
    slug: 'foundations-course',
    name: 'M.O.V.E.™ Foundations Course',
    badge: 'Self-Paced',
    price: '$197',
    priceNote: 'lifetime access',
    duration: '18 lessons · 4 modules · Start anytime',
    tagline: 'Complete the full framework on your own schedule.',
    image: '/images/programs/foundations-course.jpg',
    imageAlt: 'A woman learning on her laptop at home at her own pace',
    color: 'from-[#C9A962] to-[#A0853D]',
    accent: '#A0853D',
    intro:
      'The complete M.O.V.E.™ framework delivered as 18 structured video lessons you move through at your own pace. Select your grief pathway at enrollment and the content tailors to your specific type of loss.',
    glance: [
      { label: 'Format', value: 'Self-paced video course' },
      { label: 'Lessons', value: '18 across 4 modules' },
      { label: 'Access', value: 'Lifetime, any device' },
      { label: 'Start', value: 'Immediately after enrollment' },
    ],
    includes: [
      '18 structured video lessons covering all four M.O.V.E.™ modules',
      'Pathway selection at enrollment — content tailors to your type of loss',
      'Complete M.O.V.E.™ Participant Workbook with reflection prompts',
      'Module assessments to track your growth and progress',
      'Downloadable resources and exercises for each lesson',
      'Lifetime access — revisit any module at any time, on any device',
    ],
    audienceTitle: null,
    audience: null,
    curriculum: {
      title: 'Course Architecture',
      subtitle: 'Four modules with structured video lessons',
      type: 'modules',
    },
    note: 'Upgrade to the M.O.V.E.™ Cohort Experience at any time — your course credit is applied.',
    ctaLabel: 'Enroll Now',
  },
  {
    slug: 'workshop-series',
    name: 'M.O.V.E.™ Workshop Series',
    badge: '4-Week Series',
    price: '$747',
    priceNote: 'payment plans available',
    duration: '4 weeks · 90 min/session · Live',
    tagline: 'Four weeks of live, structured grief transformation.',
    image: '/images/programs/workshop-series.jpg',
    imageAlt: 'Facilitators leading a live group workshop session',
    color: 'from-[#2D7D7B] to-[#1F5A58]',
    accent: '#2D7D7B',
    intro:
      'A four-week, live, facilitated journey through the complete M.O.V.E.™ framework — one module each week — with reflection exercises, peer community, and pathway-specific language for your type of loss.',
    glance: [
      { label: 'Format', value: 'Live facilitated series' },
      { label: 'Length', value: '4 weeks · 90 min/session' },
      { label: 'Community', value: 'Group discussion access' },
      { label: 'Completion', value: 'Certificate awarded' },
    ],
    includes: [
      '4 live 90-minute facilitated sessions with Dr. Angelia Griffin',
      'M.O.V.E.™ Participant Workbook — digital',
      'Weekly reflection exercises and integration prompts',
      'Group discussion and peer community access during the series',
      'Completion certificate upon finishing all four sessions',
      'Pathway-specific language for your type of loss',
    ],
    audienceTitle: null,
    audience: null,
    curriculum: {
      title: 'The Four Sessions',
      subtitle: 'One M.O.V.E.™ module each week',
      type: 'sessions',
    },
    note: 'Workshop Series graduates receive a credit toward the M.O.V.E.™ Cohort Experience.',
    ctaLabel: 'Reserve Your Spot',
  },
  {
    slug: 'intensive',
    name: 'M.O.V.E.™ Intensive Experience',
    badge: 'Full-Day Intensive',
    price: '$297',
    priceNote: 'per person',
    duration: 'Full-day · In-person or virtual',
    tagline: 'The complete framework in one powerful immersive day.',
    image: '/images/programs/intensive.jpg',
    imageAlt: 'Colleagues engaged in a focused full-day group discussion',
    color: 'from-[#1B365D] to-[#2C4A7A]',
    accent: '#1B365D',
    intro:
      'All four M.O.V.E.™ modules delivered in a single, immersive day of live facilitation — ideal for organizations, retreats, and individuals who want the complete framework in one focused experience.',
    glance: [
      { label: 'Format', value: 'Full-day, in-person or virtual' },
      { label: 'Scope', value: 'All four modules in one day' },
      { label: 'Materials', value: 'Workbook included' },
      { label: 'Measurement', value: 'Before & after assessment' },
    ],
    includes: [
      'Full-day live facilitation — all four M.O.V.E.™ modules in one immersive session',
      'M.O.V.E.™ Participant Workbook (physical for in-person, digital for virtual)',
      'M.O.V.E.™ Assessment — before and after to measure your movement',
      'Graduation certificate and completion documentation',
    ],
    audienceTitle: "Who It's For",
    audience: [
      'Corporate & organizational wellness days and team offsites',
      'Retreat centers, faith communities, and hospital programs',
      'Individuals who want the full framework in a single immersive day',
    ],
    curriculum: {
      title: 'The Day at a Glance',
      subtitle: 'All four modules in one immersive session',
      type: 'modules',
    },
    note: 'Group & organizational rates available.',
    ctaLabel: 'Book the Intensive',
  },
  {
    slug: 'cohort',
    name: 'M.O.V.E.™ Cohort Experience',
    badge: 'Flagship Program',
    price: '$1,497',
    priceNote: 'payment plans available',
    duration: '8 weeks · Weekly 90-min live sessions',
    tagline: 'Eight weeks of live transformation, community & deep reintegration.',
    image: '/images/programs/cohort.jpg',
    imageAlt: 'A joyful community sharing a heartfelt embrace of support',
    color: 'from-[#1B365D] to-[#2D7D7B]',
    accent: '#2D7D7B',
    featured: true,
    intro:
      'The flagship M.O.V.E.™ program: eight weeks of live, guided transformation with Dr. Griffin, a private cohort community, weekly accountability, and pathway-specific breakout discussions — the highest level of support and integration.',
    glance: [
      { label: 'Format', value: 'Live cohort program' },
      { label: 'Length', value: '8 weeks · weekly 90-min sessions' },
      { label: 'Community', value: 'Private cohort + breakouts' },
      { label: 'Enrollment', value: 'Opens quarterly' },
    ],
    includes: [
      'Full M.O.V.E.™ curriculum — all 4 modules',
      '8 live 90-minute sessions with Dr. Griffin',
      'Digital workbook & M.O.V.E.™ assessment',
      'Private cohort community access',
      'Weekly accountability & implementation support',
      'Pathway-specific breakout discussions',
      'Graduation certificate & recognition',
    ],
    audienceTitle: null,
    audience: null,
    curriculum: {
      title: 'The 8-Week Journey',
      subtitle: 'Two weeks of depth on each M.O.V.E.™ module',
      type: 'weeks',
    },
    note: 'Cohort graduates receive priority access to the P.U.R.P.O.S.E.™ Accelerator.',
    ctaLabel: 'Apply for the Next Cohort',
  },
];

export const getProgram = (slug) => PROGRAMS.find((p) => p.slug === slug);
